import { useEffect, useMemo, useRef, useState } from 'react';
import { assetsPlaceholder } from '../../../../../constants/assetsPlaceholder';
import { TEAM_ENTITY } from '../../../../../constants/entityTypes';
import { KNOCKOUT_GAME_MIN_WIDTH } from '../../../../../constants/generic';
import { resultTypeForDisplaying } from '../../../../../constants/playoff';
import { Sports } from '../../../../../constants/sportType';
import { NOT_STARTED } from '../../../../../constants/statusTypes';
import { IBaseTeam, IPlayoffEvent, IPlayoffResult, IPlayoffRoundGroup } from '../../../../../types';
import { EventDate, GameScore, GameTeam, SMPFlex, SMPLinkWrapper, SMPText } from '../../../../index';
import format from 'date-fns/format';

export interface IKnockoutGameProps {
  group: IPlayoffRoundGroup;
  handleEventClick?: (id: string | number) => void;
  sport?: string;
  isNational?: boolean;
  groupName?: string;
  isMobile?: boolean;
  showAggEvents?: (show: boolean) => void;
  handleOpenMenu?: (value: boolean) => void;
  parentRefHeight?: number;
  handleEnableScroll?: (value: boolean) => void;
}

const mapAggEvents = (aggEvents: IPlayoffEvent[]) => {
  return aggEvents.map((event) => {
    const participantsCodes = event.participants?.map((participant) => participant.threeLetterCode);
    const formattedDate = format(new Date(event.scheduledStartTime ?? ''), 'dd.MM');
    const gameResults = event.results
      ?.filter((result) => result.resultType?.type === 'FINALRESULT')
      .map((result) => {
        return result.participants?.map((participant) => participant.value);
      });

    const winnerIndex =
      gameResults &&
      gameResults[0] &&
      gameResults[0][0] &&
      gameResults[0][1] &&
      gameResults[0][0] !== undefined &&
      gameResults[0][1] !== undefined &&
      gameResults[0][0] > gameResults[0][1]
        ? 0
        : 1;

    return {
      participantsCodes,
      formattedDate,
      gameResults,
      winnerIndex,
    };
  });
};

export const KnockoutGame = ({
  group,
  handleEventClick,
  sport,
  isNational,
  groupName,
  isMobile,
  showAggEvents,
  handleOpenMenu,
  parentRefHeight,
  handleEnableScroll,
}: IKnockoutGameProps): JSX.Element => {
  const groupResult = group?.results?.find((result: IPlayoffResult) => result?.resultType?.type === resultTypeForDisplaying) || null;
  const firstTeam = (group && group.participants && group.participants[0]) || null;
  const secondTeam = (group && group.participants && group.participants[1]) || null;
  const [showEventList, setShowEventList] = useState(false);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const dropdownEventsRef = useRef<HTMLDivElement | null>(null);

  const showEvents = () => {
    setShowEventList(!showEventList);
    handleOpenMenu && handleOpenMenu(!showEventList);
    showAggEvents && showAggEvents(!showEventList);
  };

  const mappedAggEvents = mapAggEvents(group?.events || []);

  const upcomingMatchDate = useMemo(() => {
    if (!group.events) {
      return null;
    }

    const firstEvent = group.events[0];

    if (!firstEvent || firstEvent.status?.type !== NOT_STARTED || !firstEvent.scheduledStartTime) {
      return null;
    }

    return <EventDate startTime={firstEvent.scheduledStartTime} />;
  }, [group.events]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowEventList(false);
        handleOpenMenu && handleOpenMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [containerRef]);

  useEffect(() => {
    if (showEventList && containerRef.current && dropdownEventsRef.current && parentRefHeight) {
      const containerRect = containerRef?.current?.getBoundingClientRect();
      const dropdownHeight = dropdownEventsRef?.current?.getBoundingClientRect()?.height + 38;
      const availableSpaceBelow = parentRefHeight - containerRect.bottom;

      if (availableSpaceBelow < dropdownHeight) {
        handleEnableScroll && handleEnableScroll(true);
      }
    }
    return () => {
      handleEnableScroll && handleEnableScroll(false);
    };
  }, [showEventList]);

  return (
    <div ref={containerRef} style={{ marginBottom: '8px', display: 'grid', position: 'relative' }}>
      <SMPLinkWrapper
        href={group.matchLink ?? undefined}
        target={group.urlTarget}
        onClick={() => handleEventClick && handleEventClick(group.id)}
      >
        <SMPFlex
          variant='livescoreMatch'
          style={{
            border: '1px solid var(--colors-knockoutGameBorderColor)',
            borderRadius: showEventList ? '8px 8px 0 0' : '8px',
            width: '100%',
            paddingTop: '4px',
            paddingBottom: '4px',
            paddingLeft: '16px',
            paddingRight: '16px',
            minWidth: KNOCKOUT_GAME_MIN_WIDTH + 'px',
            backgroundColor: showEventList ? 'var(--colors-gray12)' : 'var(--colors-knockoutGameBgColor)',
            overflow: 'hidden',
            borderBottom: showEventList ? 'none' : '1px solid var(--colors-knockoutGameBorderColor)',
            marginBottom: showEventList ? '1px' : '0px',
          }}
          justify={'spaceBetween'}
        >
          <SMPFlex variant='column' align='start'>
            {[firstTeam, secondTeam].map((team, index) => {
              const isWinner = team?.winner?.value === 'YES' ? true : team?.winner?.value === 'NO' ? false : undefined;
              const logo = team?.entityType === TEAM_ENTITY ? assetsPlaceholder[TEAM_ENTITY] : assetsPlaceholder.tennisParticipant;
              return (
                <GameTeam
                  isWinner={isWinner}
                  key={`${team?.id}${index}`}
                  imageVariant={sport === Sports.TENNIS && !team?.logo ? 'tennisParticipantPlayoffLogo' : 'mediumLivescoreEntityLogo'}
                  teamName={team?.name}
                  teamLogo={team?.logo ?? logo}
                  team={team as IBaseTeam}
                  isNational={isNational}
                  winnerId={isWinner ? team?.id?.toString() : undefined}
                  sport={sport}
                  showAggEvents={showEvents}
                  showAggEventsList={showEventList}
                />
              );
            })}
          </SMPFlex>
          {groupResult && groupResult.participants && groupResult.participants.length > 0 && (
            <SMPFlex css={{ paddingLeft: '$3' }}>
              {group.events?.[0].status?.type === NOT_STARTED ? (
                upcomingMatchDate
              ) : (
                <GameScore
                  eventId={group.id}
                  verticalAlign
                  homeTeamScore={groupResult.participants.find((participant) => participant.id === firstTeam?.id)?.value}
                  awayTeamScore={groupResult.participants.find((participant) => participant.id === secondTeam?.id)?.value}
                  groupName={groupName}
                  sport={sport || ''}
                  isMobile={isMobile}
                  showAggEvents={showEventList}
                />
              )}
            </SMPFlex>
          )}
        </SMPFlex>
      </SMPLinkWrapper>
      {showEventList ? (
        <SMPFlex
          ref={dropdownEventsRef}
          style={{
            width: '100%',
            position: 'absolute',
            top: '100%',
            left: 0,
            flexDirection: 'column',
            display: 'flex',
            border: '1px solid var(--colors-knockoutAggEventsBorderColor)',
            borderRadius: '0 0 8px 8px',
            minWidth: KNOCKOUT_GAME_MIN_WIDTH + 'px',
            overflow: 'hidden',
            zIndex: 10, // Ensure it appears above other elements
            borderTop: '1px solid var(--colors-knockoutGameBorderColor)', // Adding top border to the dropdown
          }}
        >
          {mappedAggEvents.map((event, key) => (
            <SMPFlex
              key={`${key}-${event.formattedDate}`}
              style={{
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                padding: '13px 16px',
                gap: '8px',
                display: 'flex',
                borderBottomLeftRadius: key === mappedAggEvents.length - 1 ? '8px' : '0px',
                borderBottomRightRadius: key === mappedAggEvents.length - 1 ? '8px' : '0px',
                borderTop: key !== 0 ? '1px solid var(--colors-knockoutAggEventsBorderColor)' : 'none',
                backgroundColor: 'var(--colors-knockoutAggEventsBgColor)',
              }}
            >
              <SMPText
                style={{
                  fontWeight: 400,
                  fontSize: '12px',
                  display: 'flex',
                  flexDirection: 'column-reverse',
                  height: '13px',
                  color: 'var(--colors-gray7)',
                }}
              >
                {event.formattedDate}
              </SMPText>
              <SMPText
                size='3'
                style={{
                  fontWeight: event.winnerIndex === 0 ? 700 : 400,
                  color: 'var(--colors-hiContrast)',
                }}
              >
                {`${event.participantsCodes && event.participantsCodes[0]}`}
              </SMPText>
              <SMPText size='3' style={{ fontWeight: 400, color: 'var(--colors-hiContrast)' }}>
                -
              </SMPText>
              <SMPText
                size='3'
                style={{
                  fontWeight: event.winnerIndex === 1 ? 700 : 400,
                  flexGrow: 1,
                  color: 'var(--colors-hiContrast)',
                }}
              >
                {`${event.participantsCodes && event.participantsCodes[1]}`}
              </SMPText>
              <SMPText
                size='3'
                style={{
                  fontWeight: 400,
                  textAlign: 'right',
                  color: 'var(--colors-hiContrast)',
                }}
              >{`${event?.gameResults && event?.gameResults[0] && event.gameResults[0][0]} - ${
                event.gameResults && event?.gameResults[0] && event.gameResults[0][1]
              }`}</SMPText>
            </SMPFlex>
          ))}
        </SMPFlex>
      ) : undefined}
    </div>
  );
};
